/**
 * Optisuite Ghost Theme - Internationalization (i18n)
 * Handles client-side translation and language switching.
 * 
 * SUPPORTED LANGUAGES: nl, fr, de, it (+ en as default)
 * To add/remove languages, see: LANGUAGES.md
 */

'use strict';

// ============================================================
// CONFIGURATION & TRANSLATIONS
// ============================================================

import { TRANSLATIONS, DEFAULT_LANG } from './translations.js';

// ============================================================
// I18N MANAGER CLASS
// ============================================================

class I18nManager {
    constructor() {
        this.rawSiteUrl = window.ghostSiteUrl || '';
        this.siteUrl = this.rawSiteUrl.replace(/\/$/, '');
        this.supportedLangs = Object.keys(TRANSLATIONS);
        
        // Parse site URL once for localization logic
        try {
            const urlObj = new URL(this.rawSiteUrl);
            this.baseDomain = `${urlObj.protocol}//${urlObj.host}`;
            this.basePath = urlObj.pathname.replace(/\/$/, '');
        } catch (e) {
            this.baseDomain = this.siteUrl;
            this.basePath = '';
        }

        this.currentLang = this.detectLanguage();
        this.boundGlobalEvents = false;
        
        // Initialize when DOM is ready
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.init());
        } else {
            this.init();
        }
    }

    /**
     * Main initialization method
     */
    init() {
        this.updateHtmlLang();
        this.localizeNavigation();
        this.renderLanguageSwitcher();
        this.updateLanguageDropdown();
        
        // Only translate UI if not default language
        if (this.currentLang !== DEFAULT_LANG) {
            this.translatePage();
        }
    }

    /**
     * Detects language based on URL path relative to site root
     */
    detectLanguage() {
        try {
            const currentUrl = window.location.href;

            for (const lang of this.supportedLangs) {
                if (currentUrl.startsWith(`${this.siteUrl}/${lang}/`) || currentUrl === `${this.siteUrl}/${lang}`) {
                    return lang;
                }
            }
            
            return DEFAULT_LANG;
        } catch (e) {
            console.warn('I18n: Detection error, defaulting to English', e);
            return DEFAULT_LANG;
        }
    }


    /**
     * Translates all marked elements on the page
     */
    translatePage() {
        document.querySelectorAll('[data-trans]').forEach(el => {
            this.translateElement(el, el.getAttribute('data-trans'));
        });

        document.querySelectorAll('[data-trans-placeholder]').forEach(el => {
            const key = el.getAttribute('data-trans-placeholder');
            const translation = this.getTranslation(key);
            if (translation) el.placeholder = translation;
        });
    }

    /**
     * Translates a single element
     */
    translateElement(el, key) {
        const translation = this.getTranslation(key);
        if (!translation) return;

        if (el.children.length === 0) {
            el.textContent = translation;
            return;
        }

        // Only replace the first text node found
        for (const node of el.childNodes) {
            if (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) {
                node.textContent = translation;
                break;
            }
        }
    }

    /**
     * Helper to get translation string
     */
    getTranslation(key) {
        return TRANSLATIONS[this.currentLang]?.[key];
    }

    /**
     * Updates the HTML lang attribute
     */
    updateHtmlLang() {
        document.documentElement.lang = this.currentLang;
    }

    /**
     * Localizes navigation links to include language prefix
     */
    localizeNavigation() {
        if (this.currentLang === DEFAULT_LANG) return;

        // Localize logo link
        const logoLink = document.querySelector('.gh-head-logo');
        if (logoLink) this.localizeLink(logoLink);

        // Localize navigation menu links
        document.querySelectorAll('.gh-head-menu a').forEach(link => this.localizeLink(link));
    }

    /**
     * Helper to localize a single link
     */
    localizeLink(link) {
        const href = link.getAttribute('href');
        
        // Skip non-internal links (must start with base domain)
        if (!href || 
            href.startsWith('#') || 
            href.startsWith('mailto:') || 
            href.startsWith('tel:') ||
            !href.startsWith(this.baseDomain)) {
            return;
        }

        // Skip if already localized
        if (href.includes(`/${this.currentLang}/`)) {
            return;
        }

        try {
            const url = new URL(href);
            
            if (url.pathname === '/' || url.pathname === '') {
                // Root domain case -> /de/
                url.pathname = `/${this.currentLang}/`;
            } else if (this.basePath && url.pathname.startsWith(this.basePath)) {
                // Subdirectory case (e.g. /blog/...) -> /blog/de/...
                const pathAfterBase = url.pathname.substring(this.basePath.length);
                url.pathname = `${this.basePath}/${this.currentLang}${pathAfterBase}`;
            } else {
                // Standard case -> /de/path
                url.pathname = `/${this.currentLang}${url.pathname}`;
            }
            
            link.setAttribute('href', url.toString());
        } catch (e) {
            // Fallback
            const newHref = href.replace(this.siteUrl, `${this.siteUrl}/${this.currentLang}`);
            link.setAttribute('href', newHref);
        }
    }

    /**
     * Generates the language switcher HTML dynamically
     */
    renderLanguageSwitcher() {
        const menuList = document.querySelector('.language-menu ul');
        if (!menuList) return;

        menuList.innerHTML = '';

        // 1. Add Default Language
        this.appendLanguageOption(menuList, DEFAULT_LANG, this.siteUrl);

        // 2. Add other languages
        this.supportedLangs.forEach(lang => {
            this.appendLanguageOption(menuList, lang, `${this.siteUrl}/${lang}/`);
        });
    }

    /**
     * Helper to append a language option
     */
    appendLanguageOption(parent, lang, url) {
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.href = url;
        a.className = 'language-option';
        a.textContent = lang.toUpperCase();
        
        li.appendChild(a);
        parent.appendChild(li);
    }

    /**
     * Sets up the language switcher dropdown
     */
    updateLanguageDropdown() {
        const dropdown = document.querySelector('.language-dropdown');
        const toggle = document.querySelector('.language-toggle');
        const label = document.querySelector('[data-lang-label]');
        
        if (!dropdown || !toggle) return;

        // Update label
        if (label) label.textContent = this.currentLang.toUpperCase();

        // Bind options
        document.querySelectorAll('.language-option').forEach(opt => {
            const optLang = opt.textContent.trim().toLowerCase();
            
            if (optLang === this.currentLang) {
                opt.classList.add('active');
            }
            
            opt.addEventListener('click', () => {
                document.documentElement.lang = optLang;
            });
        });

        this.bindDropdownEvents(dropdown, toggle);
    }

    /**
     * Binds click/key events for the dropdown
     */
    bindDropdownEvents(dropdown, toggle) {
        toggle.addEventListener('click', (e) => {
            e.stopPropagation();
            const isActive = dropdown.classList.contains('active');
            this.toggleDropdown(dropdown, toggle, !isActive);
        });

        if (!this.boundGlobalEvents) {
            document.addEventListener('click', (e) => {
                if (!dropdown.contains(e.target)) {
                    this.toggleDropdown(dropdown, toggle, false);
                }
            });

            document.addEventListener('keydown', (e) => {
                if (e.key === 'Escape') {
                    this.toggleDropdown(dropdown, toggle, false);
                }
            });
            
            this.boundGlobalEvents = true;
        }
    }

    /**
     * Helper to open/close dropdown
     */
    toggleDropdown(dropdown, toggle, show) {
        if (show) {
            dropdown.classList.add('active');
            toggle.classList.add('active');
        } else {
            dropdown.classList.remove('active');
            toggle.classList.remove('active');
        }
    }
}

// Instantiate
new I18nManager();
